// Define the structure for the Booking Engine data
interface PropertyImage {
  uid: string;
  originalName?: string;
  mimeType?: string;
}

interface LogoImageUrls {
  big: string;
  medium: string;
  small: string;
  square: string; // If you have a square URL as mentioned
}

interface LogoImage {
  uid: string;
  originalName: string;
  mimeType: string;
  urls: LogoImageUrls;
}

interface MainImage {
  uid: string;
  originalName: string;
  mimeType: string;
  urls: LogoImageUrls;
}

interface Comment {
  uid: string;
  // Add other properties if necessary
}

interface Building {
  uid: string;
  name: string;
  // Add other properties if necessary
}

export interface BookingEngineDataInterface {
  uid: string;
  name: string;
  mainTitle: string;
  accentColor: string;
  backgroundColor: string;
  bookingMode: string;
  buildings: Building[];
  buildingsCount: number;
  comments: Comment[];
  complianceLink?: string | null;
  contactEmail: string;
  contactPhone1: string;
  contactPhone2?: string | null;
  customHtmlCodeHome?: string | null;
  customHtmlCodeProperties?: string | null;
  customHtmlCodeProperty?: string | null;
  description: string;
  domains?: string[];
  enabled: boolean;
  fontSizeExtraLarge: string;
  fontSizeExtraSmall: string;
  fontSizeLarge: string;
  fontSizeMedium: string;
  fontSizeSmall: string;
  headerMarginTop: string;
  headersFont: string;
  images: PropertyImage[];
  linksFont: string;
  locationsEnabled: boolean;
  logoImage: LogoImage;
  mainImage: MainImage;
  metaAuthor: string;
  metaDescription: string;
  metaKeywords: string;
  pageTitle: string;
  petsEnabled: boolean;
  primaryColor: string;
  privacyLink: string;
  propertyTitleType?: string | null;
  rentalGuardianEnabled: boolean;
  secondaryColor: string;
  secondaryImage?: PropertyImage | null;
  tertiaryColor: string;
  textColor: string;
  textsFont: string;
  themeName?: string | null;
}

export type BookingQuoteContextType = {
  quote: BookingQuoteInputs;
  setQuote(arg: BookingQuoteInputs): void;
  reset(): void;
};

export enum InputKeys {
  PROPERTY_UID = "propertyUid",
  CHECK_IN = "fromDate",
  CHECK_OUT = "toDate",
  FIRST_NAME = "firstName",
  LAST_NAME = "lastName",
  PHONE_NUMBER = "phoneNumber",
  EMAIL = "eMail",
  ADDRESS = "address",
  CITY = "city",
  STATE = "state",
  POSTAL_CODE = "postalCode",
  COUNTRY = "country",
  ADULTS = "adults",
  CHILDREN = "children",
  PETS = "withPets",
  SMOKERS = "smokers",
  TRAVEL_INSURANCE_PRODUCT = "travelInsuranceProduct",
  GUEST_MESSAGE = "guestMessage",
}

export interface BookingQuoteInputs {
  [InputKeys.PROPERTY_UID]?: string;
  [InputKeys.CHECK_IN]?: string;
  [InputKeys.CHECK_OUT]?: string;
  [InputKeys.FIRST_NAME]?: string;
  [InputKeys.LAST_NAME]?: string;
  [InputKeys.PHONE_NUMBER]?: string;
  [InputKeys.EMAIL]?: string;
  [InputKeys.ADDRESS]?: string;
  [InputKeys.CITY]?: string;
  [InputKeys.STATE]?: string;
  [InputKeys.POSTAL_CODE]?: string;
  [InputKeys.COUNTRY]?: string;
  [InputKeys.ADULTS]?: number;
  [InputKeys.CHILDREN]?: number;
  [InputKeys.PETS]?: boolean;
  [InputKeys.SMOKERS]?: boolean;
  [InputKeys.TRAVEL_INSURANCE_PRODUCT]?: string | { data: { account: string } };
  [InputKeys.GUEST_MESSAGE]?: string;
}

export enum BookingQuoteActions {
  setQuote = "setQuote",
  reset = "reset",
}

export type BookingQuoteActionType =
  | { type: BookingQuoteActions.setQuote; value: {} }
  | { type: BookingQuoteActions.reset };
